import React from 'react'
import { useDispatch } from 'react-redux'

import ButtonIcon from '@/components/ui/ButtonIcon/ButtonIcon'
import EmailNotConfirmed from '@/components/modal-provider/global-modal/email-not-confirmed'
import ShareService from '@/services/share'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import { EyeIcon, ShareIcon } from '@/assets/icons'
import { followOrUnfollow } from '@/store/markets'
import { i18n } from '@/locale/i18n'
import { questionDetailUrlById } from '@/urls'
import { toastService } from '@/api/toast'
import { useCurrentUser, useOpenLogin } from '@/hooks'
import { useModal } from '@/components/modal-provider/ModalProvider'

/**
 * Follow toggle for a market. Guests are sent to login and users with an
 * unconfirmed email are prompted to confirm before the follow is persisted.
 * `onInterrupt` runs before either of those surfaces opens, so a caller
 * rendering the toggle inside its own overlay can dismiss it first.
 */
export const useMarketFollow = (question, currentUser, { onInterrupt } = {}) => {
  const dispatch = useDispatch()
  const { openLogin } = useOpenLogin()
  const { showModal } = useModal()
  const currentUserFromHook = useCurrentUser()
  const effectiveCurrentUser = currentUser ?? currentUserFromHook

  return async () => {
    if (!effectiveCurrentUser?.id) {
      onInterrupt?.()
      openLogin()
      return
    }
    if (!effectiveCurrentUser.email_confirmed) {
      onInterrupt?.()
      showModal({
        title: ' ',
        showCloseButton: true,
        content: <EmailNotConfirmed />,
      })
      return
    }
    const result = await dispatch(followOrUnfollow(question.id))
    if (result?.error) return
    toastService('marketFollow', {
      marketTitle: question.title,
      picture: question.outcomes[0].thumbnail,
      is_followed: question.is_following,
    })
  }
}

export const MarketFollowButton = ({ question, inMarketPage = false, currentUser }) => {
  const { openLogin } = useOpenLogin()
  const currentUserFromHook = useCurrentUser()
  const effectiveCurrentUser = currentUser ?? currentUserFromHook
  const toggleFollow = useMarketFollow(question, currentUser)

  const handleFollowClick = e => {
    e.preventDefault()
    e.stopPropagation()
    if (!effectiveCurrentUser?.id) {
      openLogin()
      return
    }
    toggleFollow()
  }

  return (
    <ButtonIcon
      size="small"
      variant={inMarketPage ? 'outline' : 'normal'}
      cursorPointer
      highlightOnHover
      handleOnClick={handleFollowClick}
      ariaLabel={i18n(question.is_following ? 'markets.following' : 'markets.follow')}>
      <EyeIcon outline={!question.is_following} />
    </ButtonIcon>
  )
}

/**
 * Shares the market through the native share sheet on touch layouts and falls
 * back to copying the link elsewhere.
 */
export const useMarketShare = question => {
  const isMobileSize = useIsMobileSize()

  return () => {
    const shareUrl = ShareService.shareableUrl(window.location.pathname)

    const copyLink = async () => {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl)
      }
      toastService('success', { title: i18n('snackbar.copylink'), body: i18n('markets.link_copied') })
    }

    if (isMobileSize) {
      if (navigator.share) {
        navigator
          .share({
            title: question.title,
            url: questionDetailUrlById(question.id),
          })
          .catch(() => copyLink())
      } else {
        copyLink()
      }
    } else {
      copyLink()
    }
  }
}

export const MarketShareButton = ({ question }) => {
  const handleShareClick = useMarketShare(question)

  return (
    <ButtonIcon
      size="small"
      variant="outline"
      cursorPointer
      highlightOnHover
      handleOnClick={handleShareClick}
      ariaLabel={i18n('markets.share_market')}>
      <ShareIcon />
    </ButtonIcon>
  )
}

const MarketShareFollow = ({ question, inMarketPage = false, currentUser }) => (
  <div className="flex gap-2 items-center">
    <MarketShareButton question={question} />
    <MarketFollowButton question={question} inMarketPage={inMarketPage} currentUser={currentUser} />
  </div>
)

export default MarketShareFollow
